import { useWallet } from '@solana/wallet-adapter-react';
import type { Dapp } from '@src/dto/Dapp';
import { isRight } from 'fp-ts/Either';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useUnreadCount } from '../../contexts/AlertContext';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { useNavigationHistory } from '../../hooks/useNavigationHistory';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import {
  getVerificationFlags,
  VERIFICATION_DEADLINE,
} from '../../lib/verificationStatus';
import {
  getDappHomePath,
  getDappRouteSegmentFromPath,
  resolveVisibleDappFromRouteSegment,
} from '../../utils/dappRoutes';
import { captureAndReportError } from '../../utils/errorReporting';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { hasDappStaffAccess } from '../../utils/roleUtils';
import { SOLANA_CLUSTER } from '../../utils/solanaUtils';
import { popoverUp, SectionLabel } from '../portal';
import { AddDappActions } from './AddDappActions';
import { AppSwitcher } from './AppSwitcher';
import { useSidebarData } from './hooks/useSidebarData';
import { PublisherCard } from './PublisherCard';
import { PublisherMenu } from './PublisherMenu';
import { PANEL_TRANSITION, SettingsView } from './SettingsView';
import { AdminNav, DappNav, isActiveRoute } from './SidebarNav';
import { SidebarNavSkeleton, SidebarSwitcherSkeleton } from './SidebarSkeleton';
import { getSidebarUtilityNavItems } from './utilityNav';
import type { SidebarVerificationCard } from './VerificationRow';
import { VerificationRow } from './VerificationRow';
import { WalletPill } from './WalletPill';

export interface SidebarProps {
  className?: string;
  onLinkClick?: () => void;
}

type OpenMenu = 'apps' | 'user' | null;

export const Sidebar = ({ className, onLinkClick }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [authState, { signOut }] = useAuth();
  const {
    activePublisher,
    accessiblePublishers,
    isSwitching,
    switchPublisher,
  } = usePublisher();
  const user =
    authState.type === AuthStateType.Authenticated ? authState.user : null;
  const trpc = useTRPC();
  const {
    data: {
      publisherUserJoinId: sidebarPublisherUserJoinId,
      kybStatusData,
      dapps,
    },
    isLoading: isSidebarDataLoading,
  } = useSidebarData();
  const navigationHistory = useNavigationHistory();
  const unreadCount = useUnreadCount();
  const { connected, publicKey } = useWallet();
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [skrClaimed, setSkrClaimed] = useState(false);
  const [walletEligible, setWalletEligible] = useState(false);
  const [routeDapp, setRouteDapp] = useState<Dapp | null>(null);
  const canUseStaffDappRoutes = user
    ? hasDappStaffAccess(user.role, user.email)
    : false;
  const hasPublisherProfile = Boolean(activePublisher);

  const isOnSettingsPage = currentPath.includes('/settings/');
  const [isSettingsMode, setIsSettingsMode] = useState(isOnSettingsPage);
  const [hasInitialized, setHasInitialized] = useState(false);

  const isSidebarDataCurrent =
    sidebarPublisherUserJoinId ===
    (activePublisher?.publisherUserJoinId ?? null);
  const isLoadingData =
    isSidebarDataLoading || (Boolean(activePublisher) && !isSidebarDataCurrent);
  const visibleDapps = isSidebarDataCurrent ? dapps : [];
  const currentKybStatusData = isSidebarDataCurrent ? kybStatusData : null;
  const routeDappAlreadyVisible =
    routeDapp && visibleDapps.some((dapp) => dapp.id === routeDapp.id);
  const visibleRouteDapp = routeDappAlreadyVisible ? null : routeDapp;
  const publisherKybStatus =
    currentKybStatusData?.status ?? activePublisher?.kybStatus;
  const publisherVerification = getPublisherVerificationGate({
    status: publisherKybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const primaryDapps = useMemo(
    () =>
      visibleRouteDapp ? [visibleRouteDapp, ...visibleDapps] : visibleDapps,
    [visibleRouteDapp, visibleDapps],
  );
  const routeSelectedDapp = useMemo(
    () =>
      resolveVisibleDappFromRouteSegment(
        getDappRouteSegmentFromPath(currentPath),
        primaryDapps,
      ) ?? null,
    [currentPath, primaryDapps],
  );
  // Publisher-level pages (alerts, settings, admin) keep the last visited app
  // selected so the switcher and NAVIGATION never go blank; first app otherwise.
  const [lastSelectedDappId, setLastSelectedDappId] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (routeSelectedDapp) {
      setLastSelectedDappId(routeSelectedDapp.id);
    }
  }, [routeSelectedDapp]);
  useEffect(() => {
    setLastSelectedDappId(null);
  }, [activePublisher?.publisherUserJoinId]);
  const selectedDapp =
    routeSelectedDapp ??
    primaryDapps.find((dapp) => dapp.id === lastSelectedDappId) ??
    primaryDapps[0] ??
    null;

  useEffect(() => {
    if (hasInitialized) {
      setIsSettingsMode(isOnSettingsPage);
    }
  }, [isOnSettingsPage, hasInitialized]);

  useEffect(() => {
    setHasInitialized(true);
  }, []);

  useEffect(() => {
    const segment = getDappRouteSegmentFromPath(currentPath);

    if (!segment || !canUseStaffDappRoutes) {
      setRouteDapp(null);
      return;
    }

    const visibleDapp = resolveVisibleDappFromRouteSegment(
      segment,
      visibleDapps,
    );
    if (visibleDapp) {
      setRouteDapp(null);
      return;
    }

    let isCancelled = false;

    const loadRouteDapp = async () => {
      try {
        const routeResult = await trpc.resolveDappRouteRef.query({
          ref: segment,
          scope: 'admin',
        });
        if (isCancelled) {
          return;
        }

        if (!('_tag' in routeResult) || !isRight(routeResult)) {
          setRouteDapp(null);
          return;
        }

        const dappResult = await trpc.getDapp.query({
          dappId: routeResult.right.id,
        });
        if (isCancelled) {
          return;
        }

        if ('_tag' in dappResult && isRight(dappResult)) {
          setRouteDapp(dappResult.right);
          return;
        }

        setRouteDapp(null);
      } catch (error) {
        if (!isCancelled) {
          setRouteDapp(null);
          captureAndReportError('Failed to load route dapp:', error);
        }
      }
    };

    void loadRouteDapp();

    return () => {
      isCancelled = true;
    };
  }, [canUseStaffDappRoutes, currentPath, trpc, visibleDapps]);

  // Close the open dropdown when clicking outside of it.
  useEffect(() => {
    if (!openMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest(`[data-dropdown="${openMenu}"]`)) {
        setOpenMenu(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        (
          document.querySelector<HTMLElement>(
            `[data-dropdown="${openMenu}"] button`,
          ) ?? null
        )?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenu]);

  // Check if connected wallet has already claimed SKR and if wallet is eligible
  useEffect(() => {
    if (!connected || !publicKey) {
      setSkrClaimed(false);
      setWalletEligible(false);
      return;
    }

    const walletAddress = publicKey.toBase58();

    trpc.skrAirdrop.getSkrClaimStatus
      .query({ walletAddress, cluster: SOLANA_CLUSTER })
      .then((result) => {
        setSkrClaimed(result.claimed);
      })
      .catch(() => {
        setSkrClaimed(false);
      });

    trpc.skrAirdrop.checkWalletEligibility
      .query({ walletAddress })
      .then((result) => {
        setWalletEligible(result.eligible);
      })
      .catch(() => {
        setWalletEligible(false);
      });
  }, [connected, publicKey, trpc]);

  const getKybStatus = (): SidebarVerificationCard => {
    if (authState.type !== AuthStateType.Authenticated || !activePublisher) {
      return {
        status: 'Loading',
        title: 'Verification',
        badgeText: 'Loading',
        isActionable: false,
        show: false,
      };
    }

    const isIndividual = activePublisher.publisherType === 'Individual';
    const verificationLabel = isIndividual
      ? 'Identity Verification'
      : 'Business Verification';
    const linkTo = isIndividual
      ? '/dashboard/submit-kyc'
      : '/dashboard/submit-kyb';

    if (activePublisher.kycKybBypassEnabled) {
      return {
        status: 'Approved',
        title: verificationLabel,
        badgeText: 'Completed!',
        linkTo,
        isActionable: false,
        show: false,
      };
    }

    // Use detailed KYB status if available
    if (currentKybStatusData) {
      const flags = getVerificationFlags(currentKybStatusData);
      switch (currentKybStatusData.status) {
        case 'NotStarted':
          if (flags.hasApplicant) {
            return {
              status: 'InProgress',
              title: verificationLabel,
              badgeText: 'Continue',
              linkTo,
              isActionable: true,
              show: true,
            };
          }
          return {
            status: 'NotStarted',
            title: verificationLabel,
            badgeText: VERIFICATION_DEADLINE,
            linkTo,
            isActionable: true,
            show: true,
          };
        case 'InProgress':
          return {
            status: 'InProgress',
            title: verificationLabel,
            badgeText: 'In Progress',
            linkTo,
            isActionable: true,
            show: true,
          };
        case 'UnderReview':
          return {
            status: 'UnderReview',
            title: verificationLabel,
            badgeText: 'Pending',
            linkTo,
            isActionable: false,
            show: true,
          };
        case 'Approved':
          return {
            status: 'Approved',
            title: verificationLabel,
            badgeText: 'Completed!',
            linkTo,
            isActionable: false,
            show: false,
          };
        case 'Rejected':
          return {
            status: 'Rejected',
            title: verificationLabel,
            badgeText: 'Rejected',
            linkTo,
            isActionable: true,
            show: true,
          };
        case 'RequiresAdditionalInfo':
          return {
            status: 'RequiresAdditionalInfo',
            title: verificationLabel,
            badgeText: 'Needs Info',
            linkTo,
            isActionable: true,
            show: true,
          };
      }
    }

    // Fallback to onboarding state
    switch (authState.user.onboardingState) {
      case 'EmailVerification':
      case 'ProfileCreation':
        return {
          status: 'NotStarted',
          title: verificationLabel,
          badgeText: VERIFICATION_DEADLINE,
          linkTo,
          isActionable: true,
          show: true,
        };
      case 'KYB':
        return {
          status: 'RequiresAdditionalInfo',
          title: verificationLabel,
          badgeText: 'Action Required',
          linkTo,
          isActionable: true,
          show: true,
        };
      case 'Complete':
        return {
          status: 'InProgress',
          title: verificationLabel,
          badgeText: 'In Progress',
          linkTo,
          isActionable: true,
          show: true,
        };
      default:
        return {
          status: 'RequiresAdditionalInfo',
          title: verificationLabel,
          badgeText: 'Action Required',
          linkTo,
          isActionable: true,
          show: true,
        };
    }
  };

  const kybStatus = getKybStatus();
  const organizationName =
    activePublisher?.name || user?.fullName || 'App Name';
  const publisherAvatarUrl =
    activePublisher?.avatarThumbnailUrl || activePublisher?.avatarUrl || null;
  const adminNavItems = getSidebarUtilityNavItems(user?.role, user?.email);
  const isOnBusinessVerificationPage = currentPath === '/dashboard/submit-kyb';
  const isOnIdentityVerificationPage = currentPath === '/dashboard/submit-kyc';
  const canAccessSkrClaim = Boolean(
    !skrClaimed && connected && publicKey && walletEligible,
  );

  const toggleMenu = (menu: Exclude<OpenMenu, null>) => {
    setOpenMenu((current) => (current === menu ? null : menu));
  };

  const go = (path: string) => {
    navigate(path);
    setOpenMenu(null);
    onLinkClick?.();
  };

  const handlePublisherSwitch = async (publisherUserJoinId: string) => {
    if (publisherUserJoinId === activePublisher?.publisherUserJoinId) {
      return;
    }

    const nextPublisher = await switchPublisher(publisherUserJoinId);
    if (!nextPublisher) {
      return;
    }

    try {
      if (isOnBusinessVerificationPage || isOnIdentityVerificationPage) {
        if (
          nextPublisher.kycKybBypassEnabled ||
          nextPublisher.kybStatus === 'Approved'
        ) {
          navigate('/dashboard', { replace: true });
          return;
        }

        if (
          isOnBusinessVerificationPage &&
          nextPublisher.publisherType === 'Individual'
        ) {
          navigate('/dashboard/submit-kyc', { replace: true });
          return;
        }

        if (
          isOnIdentityVerificationPage &&
          nextPublisher.publisherType === 'Organization'
        ) {
          navigate('/dashboard/submit-kyb', { replace: true });
          return;
        }
      }

      if (
        (currentPath === '/dashboard/settings/api-keys' ||
          currentPath === '/dashboard/settings/team') &&
        !nextPublisher.canManageTeam
      ) {
        navigate('/dashboard/settings/preferences', { replace: true });
        return;
      }

      const nextDappsResult = await trpc.listDapps.query(undefined);
      if ('_tag' in nextDappsResult && isRight(nextDappsResult)) {
        const nextDapps = nextDappsResult.right;
        const dappIdFromPath = resolveVisibleDappFromRouteSegment(
          getDappRouteSegmentFromPath(currentPath),
          visibleDapps,
        )?.id;

        if (
          dappIdFromPath &&
          !nextDapps.some((dapp) => dapp.id === dappIdFromPath)
        ) {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      captureAndReportError(
        'Failed to refresh dapps after publisher switch:',
        error,
      );
    } finally {
      setOpenMenu(null);
    }
  };

  const handleSettings = () => {
    setIsSettingsMode(true);
    go(
      hasPublisherProfile
        ? '/dashboard/settings/publisher'
        : '/dashboard/settings/profile',
    );
  };

  const handleBackToApp = () => {
    setIsSettingsMode(false);
    navigate(navigationHistory.getPreviousNonSettingsPath());
    onLinkClick?.();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      onLinkClick?.();
    } catch (error) {
      captureAndReportError('Sign out failed:', error);
    } finally {
      setOpenMenu(null);
    }
  };

  return (
    <div
      className={cn(
        'relative h-full w-72 overflow-hidden text-white',
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {isSettingsMode ? (
          <SettingsView
            onBackToApp={handleBackToApp}
            onMyProfileClick={() => go('/dashboard/settings/profile')}
            shouldAnimate={hasInitialized}
            onLinkClick={onLinkClick}
            canManageTeam={activePublisher?.canManageTeam}
            hasPublisherProfile={hasPublisherProfile}
          />
        ) : (
          <motion.div
            key="main"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={PANEL_TRANSITION}
            className="absolute inset-0 flex flex-col"
          >
            {/* Padding sits inside the scroll container so focus rings on the
                first/edge rows are not clipped by its overflow. */}
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 pb-1 pt-2">
              {isLoadingData ? (
                <SidebarSwitcherSkeleton />
              ) : (
                (hasPublisherProfile || primaryDapps.length > 0) && (
                  <AppSwitcher
                    dapps={primaryDapps}
                    selectedDapp={selectedDapp}
                    isOpen={openMenu === 'apps'}
                    onToggle={() => toggleMenu('apps')}
                    onSelect={(dapp) => go(getDappHomePath(dapp))}
                    actions={
                      hasPublisherProfile ? (
                        <AddDappActions
                          isPublisherVerificationApproved={
                            publisherVerification.isApproved
                          }
                          publisherVerificationMessage={
                            publisherVerification.actionMessage
                          }
                          onNavigate={() => {
                            setOpenMenu(null);
                            onLinkClick?.();
                          }}
                        />
                      ) : null
                    }
                  />
                )
              )}

              <SectionLabel className="px-2 pb-2 pt-4">Wallet</SectionLabel>
              <WalletPill />

              {isLoadingData && <SidebarNavSkeleton />}

              {!isLoadingData && hasPublisherProfile && (
                <VerificationRow
                  kybStatus={kybStatus}
                  isActiveRoute={(path) => isActiveRoute(currentPath, path)}
                />
              )}

              {!isLoadingData && selectedDapp && (
                <>
                  <SectionLabel className="px-2 pb-2 pt-4">
                    Navigation
                  </SectionLabel>
                  <DappNav
                    dapp={selectedDapp}
                    currentPath={currentPath}
                    onLinkClick={onLinkClick}
                  />
                </>
              )}

              {!isLoadingData && adminNavItems.length > 0 && (
                <>
                  <SectionLabel className="px-2 pb-2 pt-4">Admin</SectionLabel>
                  <AdminNav
                    items={adminNavItems}
                    currentPath={currentPath}
                    onLinkClick={onLinkClick}
                  />
                </>
              )}
            </div>

            <div className="relative mx-2 mb-2 shrink-0" data-dropdown="user">
              <PublisherCard
                name={organizationName}
                role={activePublisher?.role ?? null}
                avatarUrl={publisherAvatarUrl}
                unreadCount={unreadCount}
                isMenuOpen={openMenu === 'user'}
                onToggleMenu={() => toggleMenu('user')}
                onAlertsClick={() => go('/dashboard/alerts')}
              />
              {/* After the trigger in DOM order so Tab reaches the items; positioned above it. */}
              <AnimatePresence>
                {openMenu === 'user' && (
                  <motion.div
                    key="user-menu"
                    {...popoverUp}
                    className="absolute inset-x-0 bottom-full z-40 mb-1 max-h-[70vh] origin-bottom overflow-y-auto"
                  >
                    <PublisherMenu
                      name={organizationName}
                      avatarUrl={publisherAvatarUrl}
                      role={activePublisher?.role ?? null}
                      publisherType={activePublisher?.publisherType ?? null}
                      accessiblePublishers={accessiblePublishers}
                      activePublisherUserJoinId={
                        activePublisher?.publisherUserJoinId
                      }
                      isSwitchingPublisher={isSwitching}
                      onSwitchPublisher={handlePublisherSwitch}
                      onSettings={handleSettings}
                      onSignOut={handleSignOut}
                      showSkrClaim={canAccessSkrClaim}
                      onSkrClaim={() => go('/dashboard/claim')}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
